/**
 * WorkerDashboard — dedicated landing page for role=user after sign-in.
 *
 * Loads everything in one round-trip via /api/worker/dashboard then renders
 * 4 modules:
 *   - Safety Topic (with required acknowledgement gate)
 *   - Today's Workplan(s) (incl. rotation-derived plans)
 *   - Jobs summary + link to board
 *   - Notes (recent + search jump-off)
 *
 * Designed to be the worker's home base for the day. Admin / Supervisor see
 * an info banner pointing back at /dashboard since they have a richer view.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Checkbox } from '../components/ui/checkbox';
import { Badge } from '../components/ui/badge';
import {
  ShieldAlert, ShieldCheck, Calendar, ClipboardList, FileText, Briefcase,
  ChevronRight, MapPin, Paperclip, Link as LinkIcon, Image as ImageIcon, ExternalLink,
  Loader2, AlertCircle, Inbox, Send, StickyNote,
} from 'lucide-react';
import { toast } from 'sonner';
import { SquadLeaderQuickClock } from '../components/SquadLeaderQuickClock';
import ShiftHandoffDialog from '../components/ShiftHandoffDialog';
import MyScheduleCard from '../components/MyScheduleCard';

const API = `${window.__BACKEND_URL__}/api`;
const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

const fmtAckLatency = (s) => {
  if (s == null) return null;
  if (s < 60) return `${s}s`;
  if (s < 3600) return `${Math.round(s / 60)} min`;
  return `${(s / 3600).toFixed(1)} hr`;
};

export default function WorkerDashboard() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [acking, setAcking] = useState(false);
  const [handoffDialog, setHandoffDialog] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/worker/dashboard`, { headers: authHeaders() });
      setData(r.data);
    } catch (err) {
      console.error(err);
      toast.error('Could not load your dashboard');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const acknowledgeSafetyTopic = async () => {
    if (!data?.safety_topic?.topic) return;
    setAcking(true);
    try {
      await axios.post(
        `${API}/toolbox/respond`,
        { topic_id: data.safety_topic.topic.id, acknowledged: true },
        { headers: authHeaders() },
      );
      toast.success('Safety topic acknowledged — you’re clear to start work.');
      load();
    } catch (err) {
      console.error(err);
      toast.error('Acknowledgement failed — please try again.');
    } finally {
      setAcking(false);
    }
  };

  const toggleWorkplanTask = async (inst, taskId) => {    const updated = (inst.tasks || []).map((t) =>
      t.id === taskId ? { ...t, checked: !t.checked, completed_at: !t.checked ? new Date().toISOString() : null } : t,
    );
    setData((prev) => ({
      ...prev,
      workplan_instances: prev.workplan_instances.map((i) => (i.id === inst.id ? { ...i, tasks: updated } : i)),
    }));
    try {
      await axios.put(`${API}/workplans/instances/${inst.id}`, { tasks: updated }, { headers: authHeaders() });
    } catch (err) {
      console.error(err);
      toast.error('Sync failed — reloading');
      load();
    }
  };

  const ackHandoff = async (handoffId) => {
    try {
      await axios.post(`${API}/shift-handoffs/${handoffId}/acknowledge`, {}, { headers: authHeaders() });
      load();
    } catch (err) {
      console.error(err);
      toast.error('Could not acknowledge');
    }
  };

  if (loading || !data) {
    return (
      <div className="p-8 text-center text-slate-500">
        <Loader2 className="w-6 h-6 animate-spin inline-block mr-2" />
        Loading your dashboard...
      </div>
    );
  }

  const topic = data.safety_topic?.topic;
  const ack = data.safety_topic?.ack;
  const ackLatency = fmtAckLatency(ack?.ack_latency_seconds);
  const needsAck = !!topic && !ack?.acknowledged;

  return (
    <div className="p-4 md:p-6 max-w-6xl mx-auto space-y-5" data-testid="worker-dashboard">
      <header className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">
            Good {new Date().getHours() < 12 ? 'morning' : new Date().getHours() < 18 ? 'afternoon' : 'evening'},{' '}
            {(user?.name || '').split(' ')[0] || 'there'}
          </h1>
          <p className="text-sm text-slate-500">Here’s what’s on your plate today.</p>
        </div>
      </header>

      {/* Squad leaders can clock their whole squad in/out (Jun 2026) */}
      <SquadLeaderQuickClock />

      {/* Upcoming shifts assigned to this worker (Jun 2026) */}
      <MyScheduleCard />

      {/* ── Safety Topic — gate at the top ───────────────────────────────── */}
      {topic && (
        <Card
          className={`p-5 ${
            needsAck
              ? 'border-2 border-amber-400 bg-amber-50/70 dark:bg-amber-950/30'
              : 'bg-emerald-50/60 dark:bg-emerald-950/30 border border-emerald-200 dark:border-emerald-800'
          }`}
          data-testid="safety-topic-card"
        >
          <div className="flex items-start gap-3">
            <div className="mt-1">
              {needsAck ? (
                <ShieldAlert className="w-7 h-7 text-amber-600" />
              ) : (
                <ShieldCheck className="w-7 h-7 text-emerald-600" />
              )}
            </div>
            <div className="flex-1">
              <p className="text-xs uppercase tracking-wider font-semibold text-slate-500 mb-1">
                Today’s Safety Talk
              </p>
              <h2 className="text-lg font-bold">{topic.title}</h2>
              {topic.content && (
                <p className="text-sm text-slate-700 dark:text-slate-300 mt-2 whitespace-pre-wrap leading-relaxed">
                  {topic.content}
                </p>
              )}
              {ack?.acknowledged ? (
                <div className="mt-3 flex items-center gap-2 text-sm text-emerald-700 dark:text-emerald-300">
                  <ShieldCheck className="w-4 h-4" />
                  Acknowledged {ackLatency ? `· read in ${ackLatency}` : ''}
                </div>
              ) : (
                <>
                  <div className="mt-3 flex items-center gap-2 text-sm text-amber-700 dark:text-amber-300">
                    <AlertCircle className="w-4 h-4" />
                    You must acknowledge this before starting any jobs or workplan tasks.
                  </div>
                  <Button
                    onClick={acknowledgeSafetyTopic}
                    disabled={acking}
                    className="mt-3 bg-amber-500 hover:bg-amber-600 text-white"
                    data-testid="safety-ack-btn"
                  >
                    {acking ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <ShieldCheck className="w-4 h-4 mr-1" />}
                    I’ve read and acknowledge today’s safety topic
                  </Button>
                </>
              )}
            </div>
          </div>
        </Card>
      )}

      {needsAck && (
        <p className="text-center text-amber-700 dark:text-amber-300 text-sm font-medium" data-testid="safety-ack-blocker">
          ⚠ Acknowledge the safety topic above to unlock today’s jobs and workplan.
        </p>
      )}

      {/* ── Module cards ───────────────────────────────────────────────── */}
      <div className={`grid grid-cols-1 md:grid-cols-2 gap-4 ${needsAck ? 'opacity-40 pointer-events-none' : ''}`}>
        {/* Incoming Shift Handoffs — only when worker is on a rotation and there's something to read */}
        {(data.handoffs_in || []).length > 0 && (
          <Card className="p-4 md:col-span-2 border-2 border-purple-300 dark:border-purple-700 bg-purple-50/50 dark:bg-purple-950/20" data-testid="incoming-handoffs-card">
            <h2 className="font-semibold flex items-center gap-2 mb-3">
              <Inbox className="w-5 h-5 text-purple-600" />
              Shift Handoff{(data.handoffs_in || []).length > 1 ? 's' : ''} for You
            </h2>
            <div className="space-y-2">
              {(data.handoffs_in || []).map((h) => (
                <div
                  key={h.id}
                  className={`p-3 rounded border ${
                    h.acknowledged_by_me
                      ? 'bg-white/60 dark:bg-slate-900/40 border-slate-200 dark:border-slate-700 opacity-70'
                      : 'bg-white dark:bg-slate-900 border-purple-300 dark:border-purple-700'
                  }`}
                  data-testid={`handoff-${h.id}`}
                >
                  <div className="flex items-center justify-between mb-1">
                    <p className="text-xs text-slate-500">
                      From <span className="font-medium">{h.from_user_name}</span> · for {h.for_date}
                    </p>
                    {h.acknowledged_by_me ? (
                      <span className="text-xs text-emerald-600">✓ Read</span>
                    ) : (
                      <Button size="sm" variant="outline" onClick={() => ackHandoff(h.id)} data-testid={`handoff-ack-${h.id}`}>
                        Mark as read
                      </Button>
                    )}
                  </div>
                  <p className="text-sm whitespace-pre-wrap">{h.body}</p>
                </div>
              ))}
            </div>
          </Card>
        )}

        {/* Workplan */}
        <Card className="p-4" data-testid="dashboard-workplan-card">
          <div className="flex items-center justify-between mb-3">
            <h2 className="font-semibold flex items-center gap-2">
              <ClipboardList className="w-5 h-5 text-blue-600" />
              Today’s Workplan
            </h2>
            <div className="flex items-center gap-2">
              {data.rotation && (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setHandoffDialog(true)}
                  data-testid="leave-handoff-btn"
                >
                  <Send className="w-3 h-3 mr-1" /> Leave Handoff
                </Button>
              )}
              <Badge variant="outline">{data.workplan_instances.length}</Badge>
            </div>
          </div>
          {data.workplan_instances.length === 0 ? (
            <p className="text-sm text-slate-500 italic">No workplan assigned for today.</p>
          ) : (
            <div className="space-y-3">
              {data.workplan_instances.map((inst) => {
                const tasks = inst.tasks || [];
                const done = tasks.filter((t) => t.checked).length;
                const pct = tasks.length > 0 ? Math.round((done / tasks.length) * 100) : 0;
                return (
                  <div key={inst.id} className="border rounded-md p-3" data-testid={`wd-workplan-${inst.id}`}>
                    <div className="flex items-start justify-between">
                      <div>
                        <p className="font-medium">{inst.title}</p>
                        {inst.rotation_day && (
                          <Badge className="bg-purple-500 text-white mt-1 mr-2">
                            Day {inst.rotation_day} of {inst.rotation_cycle_length}
                          </Badge>
                        )}
                        {inst.location && (
                          <p className="text-xs text-slate-500 flex items-center gap-1 mt-1">
                            <MapPin className="w-3 h-3" /> {inst.location}
                          </p>
                        )}
                      </div>
                      <span className="text-xs text-slate-500">{done}/{tasks.length}</span>
                    </div>
                    {tasks.length > 0 && (
                      <>
                        <div className="mt-2 h-1.5 bg-slate-200 dark:bg-slate-700 rounded-full overflow-hidden">
                          <div className="h-full bg-emerald-500 transition-all" style={{ width: `${pct}%` }} />
                        </div>
                        <div className="mt-3 space-y-1.5">
                          {tasks.map((t) => (
                            <label key={t.id} className="flex items-start gap-2 cursor-pointer text-sm">
                              <Checkbox
                                checked={!!t.checked}
                                onCheckedChange={() => toggleWorkplanTask(inst, t.id)}
                                className="mt-0.5"
                                data-testid={`wd-task-${t.id}`}
                              />
                              <span className={t.checked ? 'line-through text-slate-400' : ''}>{t.text}</span>
                            </label>
                          ))}
                        </div>
                      </>
                    )}
                    {(inst.attachments || []).length > 0 && (
                      <div className="mt-3 pt-3 border-t text-xs">
                        <p className="text-slate-500 mb-1 flex items-center gap-1">
                          <Paperclip className="w-3 h-3" /> Documents & Links
                        </p>
                        {(inst.attachments || []).map((a) => (
                          <a
                            key={a.id}
                            href={a.url || `data:${a.mime || 'application/octet-stream'};base64,${a.data_base64}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center gap-1 text-blue-600 hover:underline mb-0.5"
                          >
                            {a.type === 'link' && <LinkIcon className="w-3 h-3" />}
                            {a.type === 'image' && <ImageIcon className="w-3 h-3" />}
                            {(a.type === 'pdf' || a.type === 'doc') && <FileText className="w-3 h-3" />}
                            {a.name} {a.type === 'link' && <ExternalLink className="w-3 h-3" />}
                          </a>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </Card>

        {/* Jobs */}
        <Card className="p-4 cursor-pointer hover:shadow-md transition" onClick={() => navigate('/jobs/board')} data-testid="dashboard-jobs-card">
          <div className="flex items-center justify-between mb-3">
            <h2 className="font-semibold flex items-center gap-2">
              <Briefcase className="w-5 h-5 text-emerald-600" />
              Jobs
            </h2>
            <ChevronRight className="w-4 h-4 text-slate-400" />
          </div>
          <div className="grid grid-cols-4 gap-2 text-center">
            {[
              { k: 'todo', label: 'To Do', tint: 'bg-slate-100 dark:bg-slate-800' },
              { k: 'in_progress', label: 'Active', tint: 'bg-emerald-100 dark:bg-emerald-900/40' },
              { k: 'paused', label: 'Paused', tint: 'bg-amber-100 dark:bg-amber-900/40' },
              { k: 'completed', label: 'Done', tint: 'bg-blue-100 dark:bg-blue-900/40' },
            ].map((c) => (
              <div key={c.k} className={`${c.tint} rounded-md p-3`}>
                <p className="text-2xl font-bold">{data.jobs.counts[c.k] || 0}</p>
                <p className="text-xs text-slate-500">{c.label}</p>
              </div>
            ))}
          </div>
          <div className="grid grid-cols-2 gap-2 mt-3">
            <Button
              variant="outline"
              size="sm"
              className="text-xs"
              onClick={(e) => { e.stopPropagation(); navigate('/jobs/board'); }}
              data-testid="dashboard-log-tasks-btn"
            >
              <Briefcase className="w-3 h-3 mr-1" /> Log Tasks
            </Button>
            <Button
              variant="outline"
              size="sm"
              className="text-xs border-amber-500 text-amber-600 dark:text-amber-300 hover:bg-amber-500/10"
              onClick={(e) => { e.stopPropagation(); navigate('/notes'); }}
              data-testid="dashboard-notes-btn"
            >
              <StickyNote className="w-3 h-3 mr-1" /> Notes
            </Button>
          </div>
        </Card>

        {/* Notes */}
        <Card className="p-4 cursor-pointer hover:shadow-md transition md:col-span-2" onClick={() => navigate('/notes')} data-testid="dashboard-notes-card">
          <div className="flex items-center justify-between mb-3">
            <h2 className="font-semibold flex items-center gap-2">
              <FileText className="w-5 h-5 text-purple-600" />
              Notes
            </h2>
            <span className="text-sm text-slate-500 flex items-center gap-1">
              {data.notes.total} total <ChevronRight className="w-4 h-4" />
            </span>
          </div>
          {(data.notes.recent || []).length === 0 ? (
            <p className="text-sm text-slate-500 italic">
              No notes yet — open Notes to capture a reminder or send a message to a teammate.
            </p>
          ) : (
            <div className="space-y-1">
              {data.notes.recent.map((n) => (
                <div key={n.id} className="text-sm border-l-2 border-purple-300 pl-2">
                  <span className="font-medium">{n.title || '(untitled)'}</span>
                  {n.job_id && <Badge variant="outline" className="ml-2 text-xs">linked to job</Badge>}
                </div>
              ))}
            </div>
          )}
        </Card>
      </div>

      {/* Outgoing handoff dialog — only mounts when user is on a rotation */}
      {data.rotation && (
        <ShiftHandoffDialog
          open={handoffDialog}
          onOpenChange={setHandoffDialog}
          rotationId={data.rotation.id}
          rotationName={data.rotation.name}
          onSaved={load}
        />
      )}
    </div>
  );
}
